<template>
  <div class="scroll-wrapper">
    <!-- preview popup thumbnail -->
    <popup-image
      v-show="
        loadedRange && isPreviewVisible && !isMobile && showThumbnails === true
      "
      ref="preview"
      class="scroll-wrapper__popup"
      :text="previewTimeLabel"
      :image="previewImage"
      :style="previewStyle"
    />
    <!-- This block need to hide scroll overflow, but not hide popup thumbnail (how it will if it will be parent of popup) -->
    <div
      ref="timeline"
      v-dragscroll.x
      class="scroll-wrapper__scroll scroll-container"
      @dragscrollstart="isScrolling = true"
      @dragscrollend="isScrolling = false"
      @dragscrollmove="handleScrolling"
    >
      <!-- Timeline scale with labels as 23:12:45 -->
      <div
        v-if="loadedRange"
        class="timeline__scale"
        :style="{
          overflow: 'hidden',
          width: maxLabelsWidth + 'px',
          height: $options.scaleHeight + 'px',
          'margin-left': labelsLeftMargin + 'px'
        }"
      >
        <timeline-scale
          :style="{ width: labelsWidth + 'px' }"
          :start="labelsStart"
          :timestep="labelTimestep"
          :labels-count="labelsCount"
        />
      </div>

      <!-- Thumbnails, slider and breaks -->
      <div
        class="thumbnails-wrapper"
        :style="{
          width: actualThumbnailsWidth + 'px',
          'padding-left': scrollLeftMargin + 'px',
          'padding-right': scrollRightMargin + 'px'
        }"
        @mousewheel="handleMouseWheel"
      >
        <timeline-frame
          v-if="loadedRange"
          ref="scrollable"
          class="thumbnails-wrapper__content"
          :timerange="loadedRange"
          :width="actualThumbnailsWidth"
          :height="thumbnailHeight"
          :show-thumbnails="showThumbnails"
          :value-checkbox-range="valueCheckboxRange"
          :stream-start="timerange[0]"
          :stream-end="timerange[1]"
          :player-time="timelineData.playerTime"
          :slider="timelineData.slider"
          :is-slider-smooth="timelineData.isSliderSmooth"
          :breaks="timelineData.breaks"
          :has-thumbnails="hasThumbnails"
          :thumbnail-size="thumbnailSize"
          :thumbnail-duration="thumbnailTimestep"
          :scale-mode="scaleMode"
          @thumbnailWidthUpdated="thumbnailWidth = $event"
          @thumbnailTimestepUpdated="thumbnailTimestep = $event"
          @sliderDragging="previewTime = $event"
          @movePlayer="$emit('movePlayer', $event)"
          @mouseMovedToTime="handlePreviewMoved"
          @mouseleave="areThumbnailsHovered = false"
          @sliderMoved="$emit('sliderMoved', $event)"
        />
      </div>
    </div>
  </div>
</template>

<script>
import TimelineFrame from "@/components/timeline/TimelineFrame.vue";
import TimelineScale from "@/components/timeline/TimelineScale.vue";
import PopupImage from "@/components/popup/PopupImage.vue";

import { getTimeLineWithMs } from "@/utils/time.js";
import { mapGetters, mapState } from "vuex";

import { EDITOR_ID, SCALE_MODE, isScaleModeValid } from "@/constants.js";
import { alignUpByStep, alignDownByStep } from "@/utils/calc.js";

/** time constants */
const HOUR = 3600;
const MINUTE = 60;
const SECOND = 1;

import { dragscroll } from "vue-dragscroll";

// @group Timeline
// Scrollable timeline thumbnails
export default {
  name: "TimelineScrollable",
  directives: {
    dragscroll
  },
  components: {
    TimelineFrame,
    TimelineScale,
    PopupImage
  },
  props: {
    valueCheckboxRange: {
      type: Boolean,
      default: false
    },
    /** The show modal thumbnails*/
    showThumbnails: {
      type: Boolean,
      default: true
    },
    /** stream start & end timestamps */
    timerange: {
      type: Array,
      default: () => [0, 1],
      validator([start, end]) {
        return start < end;
      }
    },
    /** visible area start & end timestamps */
    visible: {
      type: Array,
      default: () => [0, 1],
      validator([start, end]) {
        return start < end;
      }
    },
    /** thumbnail aspect ratio (width / height) */
    thumbnailAspectRatio: {
      type: Number,
      default: 270 / 155
    },
    /** scale mode - defines frequency of labels and break borders width */
    scaleMode: {
      type: String,
      default: SCALE_MODE.NORMAL,
      validator(mode) {
        return isScaleModeValid(mode);
      }
    },
    /** do stream has thumbnails */
    hasThumbnails: {
      type: Boolean,
      default: true
    },
    /** additional timeline optional data: player position, slider options, break ranges, etc */
    timelineData: {
      type: Object,
      default: () => new Object()
    }
  },
  /** scale height in pixels */
  scaleHeight: 30,
  /** timeline width in pixels for average screen */
  averageTimelineWidth: 1080,
  /** minimum count of visible time labels  */
  minimumLabelsCount: 3,
  /** possible variants of time difference between two time labels */
  availableTimesteps: [
    12 * HOUR,
    6 * HOUR,
    4 * HOUR,
    3 * HOUR,
    2 * HOUR,
    HOUR,
    30 * MINUTE,
    20 * MINUTE,
    10 * MINUTE,
    5 * MINUTE,
    2 * MINUTE,
    MINUTE,
    30 * SECOND,
    20 * SECOND,
    10 * SECOND,
    5 * SECOND,
    SECOND
  ],
  editorId: EDITOR_ID,
  data() {
    return {
      /** minimum start time that timeline can be scrolled to */
      thumbnailsInitStart: this.timerange[0],
      /** start time of scale labels */
      scaleStart: this.timerange[0],
      /** timeline container size (in pixels) */
      timelineSize: {
        h: 160,
        w: 1080
      },
      /** preview time */
      previewTime: 0,
      /** block preview, to prevent preview sliding on zoom */
      blockPreview: false,
      areThumbnailsHovered: false,
      /** rendered timeline start & end (in seconds) */
      loadedRange: null,
      /** when visible in this range - there is not need to reload */
      syncRange: null,
      isMobile: false,
      isScrolling: false
    };
  },
  computed: {
    ...mapState({
      timezone: state => state.timezone.timezone
    }),
    ...mapGetters("thumbnails", ["getThumbnailLink"]),
    ...mapGetters("timeline", ["maxVisibleRight"]),
    // @vuese
    // Length of visible timeline part (in seconds)
    visibleDuration() {
      return this.visible[1] - this.visible[0];
    },
    // @vuese
    // Length of rendered timeline (in seconds)
    loadedDuration() {
      if (!this.loadedRange) return 0;
      return this.loadedRange[1] - this.loadedRange[0];
    },
    // @vuese
    // Count of seconds in one timeline pixel
    timeWidthRatio() {
      return this.visibleDuration / this.timelineSize.w;
    },

    /** thumbnails parameters */

    // @vuese
    // One thumbnail width in pixels
    thumbnailWidth() {
      return this.thumbnailHeight * this.thumbnailAspectRatio;
    },
    // @vuese
    // One thumbnail height in pixels
    thumbnailHeight() {
      return this.timelineSize.h - this.$options.scaleHeight;
    },
    // @vuese
    // Time step between two thumbnails in seconds
    thumbnailTimestep() {
      return this.thumbnailWidth * this.timeWidthRatio;
    },
    // @vuese
    // Thumbnail width and height in pixels
    thumbnailSize() {
      return {
        w: this.thumbnailWidth,
        h: this.thumbnailHeight
      };
    },
    // @vuese
    // Time of first rendering thumbnail
    thumbnailsStart() {
      const offset = this.timerange[0] - this.thumbnailsInitStart;
      /**
       * we need to align start by thumbnail time step to prevent case when all
       * thumbnails rerendered with new timestamps because of bad offset
       * */
      return (
        this.thumbnailsInitStart +
        alignDownByStep(offset, this.thumbnailTimestep)
      );
    },
    // @vuese
    // Finish time for last thumbnail
    thumbnailsEnd() {
      const offset = this.timerange[1] - this.thumbnailsInitStart;
      return (
        this.thumbnailsInitStart + alignUpByStep(offset, this.thumbnailTimestep)
      );
    },
    // @vuese
    // Rendered end time aligned by visible length's steps
    alignedLoadedEnd() {
      const leftPadding = this.loadedRange[1] - this.thumbnailsInitStart;
      const alignedPadding = alignUpByStep(leftPadding, this.thumbnailTimestep);
      return this.thumbnailsInitStart + alignedPadding;
    },
    // @vuese
    // Length of left (unvisible) rendered part (in seconds)
    scrollLeftTime() {
      if (!this.loadedRange) return 0;

      return this.visible[0] - this.loadedRange[0] + this.clearedTime;
    },
    // @vuese
    // Width of left (unvisible) rendered part (in pixels)
    scrollLeftWidth() {
      return this.scrollLeftTime / this.timeWidthRatio;
    },
    // @vuese
    // Actual width of thumbnails range
    actualThumbnailsWidth() {
      return this.loadedDuration / this.timeWidthRatio;
    },
    // @vuese
    // Width of left scroll padding that appears instead of cleared thumbnails
    scrollLeftMargin() {
      return this.clearedThumbnailsCount * this.thumbnailWidth;
    },
    // @vuese
    // Width of right padding that gives space for live pointer forward motion
    scrollRightMargin() {
      if (!this.loadedRange) return 0;

      if (this.loadedRange[1] < this.timerange[1]) return 0;

      const aligningShift = this.maxVisibleRight - this.timerange[1];
      return aligningShift / this.timeWidthRatio;
    },
    // @vuese
    // Count of thumbnails that are before stream start but we need to
    // render those as black space because user see those yet
    clearedThumbnailsCount() {
      const nowStartOffset = this.timerange[0] - this.thumbnailsInitStart;
      const offsetWidth = nowStartOffset / this.timeWidthRatio;
      const offsetThumbnailsCount = Math.floor(
        offsetWidth / this.thumbnailWidth
      );
      return offsetThumbnailsCount;
    },
    // @vuese
    // Duration of cleared area at stream start
    clearedTime() {
      return this.clearedThumbnailsCount * this.thumbnailTimestep;
    },

    // @vuese
    // Preferred count of time labels on average desktop screen (1080px)
    visibleLabelsStandardCount() {
      switch (this.scaleMode) {
        case SCALE_MODE.CLOSE:
          return 8;
        case SCALE_MODE.NEAR:
          return 8;
        case SCALE_MODE.NORMAL:
          return 8;
        case SCALE_MODE.AWAY:
          return 8;
        case SCALE_MODE.FARAWAY:
          return 10;
        default:
          return 10;
      }
    },
    // @vuese
    // How many labels should be visible on scale (depends of zoom)
    maxVisibleLabels() {
      const widthCoeff =
        this.timelineSize.w / this.$options.averageTimelineWidth;
      const sizedCount = Math.round(
        widthCoeff * this.visibleLabelsStandardCount
      );
      return Math.max(this.$options.minimumLabelsCount, sizedCount);
    },
    // @vuese
    // Time step between two scale labels in seconds
    labelTimestep() {
      const steps = this.$options.availableTimesteps;
      let minStep = steps[0];

      /** find the minimum valiable time step */
      for (const step of steps) {
        if (this.visibleDuration / step <= this.maxVisibleLabels) {
          minStep = step;
        }
      }
      return minStep;
    },
    // @vuese
    // Time of first rendering scale label
    labelsStart() {
      const alignNum = Math.min(this.labelTimestep, HOUR);
      return alignUpByStep(this.loadedRange[0], alignNum);
    },
    // @vuese
    // End time of last rendering dcale label
    labelsEnd() {
      const alignNum = Math.min(this.labelTimestep, HOUR);
      return alignUpByStep(this.loadedRange[1], alignNum);
    },
    // @vuese
    // Duration of rendering scale labels in seconds
    labelsDuration() {
      return this.labelsEnd - this.labelsStart;
    },
    // @vuese
    // Count of rendering labels
    labelsCount() {
      return Math.ceil(this.labelsDuration / this.labelTimestep);
    },
    // @vuese
    // Width of rendering scale in pixels
    labelsWidth() {
      return this.labelsDuration / this.timeWidthRatio;
    },
    // @vuese
    // Visible width of labels panel
    maxLabelsWidth() {
      return (
        (this.loadedRange[1] - this.labelsStart) / this.timeWidthRatio +
        this.scrollRightMargin
      );
    },
    // @vuese
    // Width of left scale labels start
    labelsLeftMargin() {
      const labelsOffset = this.labelsStart - this.loadedRange[0];
      const clearedOffset = this.clearedTime;
      return (labelsOffset + clearedOffset) / this.timeWidthRatio;
    },

    /** preview computeds */
    previewLeftPercentage() {
      const previewHalfWidth = this.$refs.preview?.$el?.clientWidth / 2 ?? 0;
      const previewHalfPercentage =
        (previewHalfWidth / this.timelineSize.w) * 100;
      const leftPercentage =
        ((this.previewTime - this.visible[0]) / this.visibleDuration) * 100;
      const leftBounded = Math.max(
        previewHalfPercentage,
        Math.min(100 - previewHalfPercentage, leftPercentage)
      );

      return leftBounded;
    },
    // @vuese
    // Preview position
    previewStyle() {
      return {
        left: this.previewLeftPercentage + "%",
        top: this.$options.scaleHeight + "px"
      };
    },
    // @vuese
    // Preview thumbnail text
    previewTimeLabel() {
      return getTimeLineWithMs(this.previewTime, this.timezone);
    },
    // @vuese
    // Preview thumbnail image
    previewImage() {
      return this.getThumbnailLink(this.previewTime);
    },
    // @vuese
    // Is preview time between stream start & end
    isPreviewInStreamRange() {
      return (
        this.timerange[0] <= this.previewTime &&
        this.previewTime <= this.timerange[1]
      );
    },
    // @vuese
    // To show preview popup thumbnail
    isPreviewVisible() {
      return (
        this.hasThumbnails &&
        this.areThumbnailsHovered &&
        this.isPreviewInStreamRange
      );
    }
  },
  watch: {
    timerange([start], [oldStart]) {
      if (start < oldStart) {
        /** if new start less than old, it means stream was reloaded (possibly) */
        this.thumbnailsInitStart = start;
        return;
      }

      /** update scale */
      this.updateScaleStart();

      const hasPadding = this.clearedThumbnailsCount > 0;
      const isClearedInvisible = this.thumbnailsStart <= this.visible[0];
      /** update init start if start is invisible */
      if (hasPadding && isClearedInvisible) {
        /** update only when previous thumbnails area is not visible */
        this.thumbnailsInitStart = this.thumbnailsStart;
      }
      this.syncLoaded();
    },
    // @vuese
    // Rerender current timeline when it's near to border
    visible([start, end]) {
      if (start > this.syncRange[1] || end < this.syncRange[0]) {
        this.syncLoaded();
      }
      this.updateScaleStart();
    },
    // @vuese
    // Update start when timeline zoomed
    visibleDuration(newDuration, oldDuration) {
      if (Math.abs(newDuration - oldDuration) >= 1) {
        this.thumbnailsInitStart = this.timerange[0];
        this.scaleStart = this.timerange[0];
        this.syncLoaded();

        this.blockPreview = true;
        this.areThumbnailsHovered = false;
      }
    },
    // @vuese
    // Synchronize scroll position when rendered range has changed
    loadedRange() {
      /**
       * sync scroll after tick to let Vue rerender template
       * if not to do it, then syncScrol may be failed at start
       * because of too smal width to scroll
       */
      this.$nextTick(() => this.syncScroll());
    },
    // @vuese
    // Move scroll when here aren't visible thumbnails
    clearedThumbnailsCount() {
      if (this.thumbnailsStart > this.visible[1]) {
        const shift = this.thumbnailsStart - this.visible[0];
        this.$emit("scroll", shift);
      }
    }
  },
  mounted() {
    this.updateSize();
    this.syncLoaded();
    window.addEventListener("resize", this.updateSize);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.updateSize);
  },
  methods: {
    // @vuese
    // Change loaded range to place visible in center of it
    syncLoaded() {
      this.syncRange = [...this.visible];

      /** we need to update load range so, then difference between old load start and new one
       * will be a multiple of thumbnail time step, so old thumbnails saved in new range will
       * not be re-rendered, but save it's positions
       */
      const syncStartPadding = this.syncRange[0] - this.thumbnailsInitStart;
      const loadStartPadding = alignDownByStep(
        syncStartPadding - this.visibleDuration - 1,
        this.thumbnailTimestep
      );
      const loadedStart = Math.max(
        this.thumbnailsInitStart + loadStartPadding,
        this.thumbnailsStart
      );

      const syncEndPadding = this.syncRange[1] - this.thumbnailsInitStart;
      const loadEndPadding = alignUpByStep(
        syncEndPadding + this.visibleDuration + 1,
        this.thumbnailTimestep
      );
      const loadedEnd = Math.min(
        this.thumbnailsInitStart + loadEndPadding,
        this.timerange[1]
      );

      this.loadedRange = [loadedStart, loadedEnd];
    },
    // @vuese
    // Synchronize timeline's scrollLeft with visible area start time
    syncScroll() {
      this.$refs.timeline.scrollLeft = this.scrollLeftWidth;
    },
    // @vuese
    // Update width & height data parameters with actual HTML layout
    updateSize() {
      this.isMobile = document.body.offsetWidth < 992;
      const timelineEl = this.$refs.timeline;
      if (timelineEl) {
        this.timelineSize = {
          w: timelineEl.clientWidth,
          h: timelineEl.clientHeight
        };
      } else {
        console.warn("TimelineScrollable: timeline element not found");
      }
    },
    // @vuese
    // Update external visible area parameters
    handleScrolling({ detail: { deltaX } }) {
      const deltaTime = (deltaX / this.timelineSize.w) * this.visibleDuration;
      /** update visible only when it fits to available range */

      if (
        deltaTime < 0 &&
        this.visible[0] + deltaTime < this.thumbnailsInitStart
      ) {
        return;
      }

      this.$emit("scroll", deltaTime);
    },
    // @vuese
    // Handle user's wheel scrolling (zoom in/out)
    handleMouseWheel({ deltaY }) {
      const delta = Math.sign(deltaY);
      this.$emit("zoom", {
        delta,
        pointer: this.previewTime
      });
    },
    // @vuese
    // Update preview position
    handlePreviewMoved(newTime) {
      if (this.blockPreview) {
        /** if preview was blocked the we need to only unlock it */
        this.blockPreview = false;
      } else if (!this.isScrolling) {
        /** if preview unlocked than it may be shown */
        this.previewTime = newTime;
        this.areThumbnailsHovered = true;
      }
    },
    // @vuese
    // Update scale start
    updateScaleStart() {
      const diff = this.start - this.scaleStart;
      if (diff >= this.labelTimestep) {
        const alignedDiff = alignDownByStep(diff, this.labelTimestep);
        this.scaleStart = this.start + alignedDiff;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.scroll-wrapper {
  position: relative;

  &__scroll {
    height: 100%;
  }

  &__popup {
    position: absolute;
    transform: translateY(-100%) translateX(-50%);
  }
}

.scroll-container {
  overflow: hidden;
}

.thumbnails-wrapper {
  background-color: black;

  &__content {
    height: 100%;
  }
}
</style>
